<template>
    <div class="modal-privacy">
        <div class="privacy-header">
            <span class="gradient-green">PRIVACY POLICY</span>
        </div>
        <div class="privacy-list">
            <div class="list-element">
                <div class="element-title">1. Introduction</div>
                <div class="element-text">
                    <p>Worldcupsports.casino (<a href="https://Worldcupsports.casino/" target="_blank">https://Worldcupsports.casino/</a>) ("we", "us", "our", "the Platform") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and share your information when you visit or use our website or services ("Service"), and outlines your rights with respect to that data. By using Worldcupsports.casino (<a href="https://Worldcupsports.casino/" target="_blank">https://Worldcupsports.casino/</a>), you consent to the practices described here.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">2. Information We Collect</div>
                <div class="element-text">
                    <p>We may collect and process the following categories of information:</p>
                    <p><strong>Registration & Account Data:</strong> name, date of birth, address, email, phone number, username, password, and any required identity-verification documents (e.g. passport, government ID, proof of address) if KYC is enforced.</p>
                    <p><strong>Payment & Transaction Data:</strong> payment method details, transaction history, withdrawal/deposit history, cryptocurrency wallet addresses (if applicable), or e-wallet/payment card data as needed for deposits or withdrawals.</p>
                    <p><strong>Usage Data / Device & Technical Data:</strong> IP address, geolocation (if permitted), browser and operating system info, device identifiers, login timestamps, session details, cookies, and other browsing-related data.</p>
                    <p><strong>Communications:</strong> records of support chats, emails, or other communications between you and us.</p>
                    <p><strong>Optional Data:</strong> any additional personal data you provide (e.g. when contacting support, filling out surveys, participating in promotions).</p>
                    <p>We collect only what is necessary for providing the Casino service, verifying identity, complying with legal/regulatory obligations, fraud prevention, and improving our services.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">3. How We Use Your Data</div>
                <div class="element-text">
                    <p>We use your information for the following purposes:</p>
                    <ul>
                        <li>To create, maintain, and manage your account.</li>
                        <li>To verify identity (KYC), age, and eligibility, and to comply with applicable laws/regulations (e.g., anti-money-laundering, responsible gambling).</li>
                        <li>To process deposits, wagers, and withdrawals.</li>
                        <li>To provide customer support and respond to your inquiries.</li>
                        <li>To ensure security of the platform (fraud detection, risk management).</li>
                        <li>To enforce our Terms of Service (e.g. detect abuse, collusion, prohibited activity).</li>
                        <li>To send you important notices (e.g. policy updates, account changes).</li>
                        <li>With your explicit consent (if applicable), to send marketing/promotions or notifications (bonuses, new games, offers).</li>
                    </ul>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">4. Data Sharing and Third Parties</div>
                <div class="element-text">
                    <p>We may share your personal data with the following:</p>
                    <p><strong>Service providers and partners:</strong> Payment processors, KYC/identity-verification providers, fraud-prevention services, hosting and infrastructure providers, analytics tools, and other third parties essential for operation of the Service.</p>
                    <p><strong>Regulatory or legal authorities:</strong> When required to comply with laws/regulations (e.g. AML/KYC laws) or to respond to legal requests.</p>
                    <p><strong>Third-party game providers:</strong> For games you play through the platform (if applicable), to record gameplay and handle payouts.</p>
                    <p><strong>Affiliates or marketing partners</strong> — only if you have explicitly opted in for marketing communications.</p>
                    <p>We will never sell or lease your personal data for commercial marketing without your explicit consent.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">5. Data Storage & Security</div>
                <div class="element-text">
                    <p>We implement technical and organizational measures to protect personal data against unauthorized access, loss, misuse, alteration, or disclosure. Access to your data by employees is limited to those who need it for their duties.</p>
                    <p>We retain your data only as long as necessary to provide services, comply with legal obligations (e.g. AML), resolve disputes, and maintain records for business or regulatory needs. Once no longer needed, data will be deleted or anonymized (subject to legal/data-retention requirements).</p>
                    <p>Despite our best efforts, no system can guarantee 100% security. By using Worldcupsports.casino (<a href="https://Worldcupsports.casino/" target="_blank">https://Worldcupsports.casino/</a>), you accept the inherent risks of data transmission over the internet.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">6. Cookies and Tracking Technologies</div>
                <div class="element-text">
                    <p>We may use cookies, web beacons, and similar technologies to:</p>
                    <ul>
                        <li>Recognize and authenticate you when you return;</li>
                        <li>Maintain your session, preferences, and login status;</li>
                        <li>Monitor and analyze usage patterns to improve the Platform;</li>
                        <li>Provide security (e.g. prevent fraud).</li>
                    </ul>
                    <p>If you are in a jurisdiction where consent is required (e.g. under General Data Protection Regulation, GDPR), we will obtain your consent before placing non-essential cookies.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">7. Your Rights (Data Subject Rights)</div>
                <div class="element-text">
                    <p>Depending on your location and applicable law (e.g. GDPR, California Consumer Privacy Act — CCPA / California Privacy Rights Act — CPRA), you may have the following rights:</p>
                    <ul>
                        <li>Request access to the personal data we hold about you.</li>
                        <li>Request correction of inaccurate or incomplete personal data.</li>
                        <li>Request deletion or anonymization of your personal data (to the extent consistent with legal or regulatory obligations, including AML and record-keeping laws).</li>
                        <li>Object to or restrict processing of your data (where applicable).</li>
                        <li>Request data portability — a machine-readable copy of your personal data.</li>
                        <li>Revoke consent (for processing based on consent) at any time; this will not affect processing already carried out lawfully before such revocation.</li>
                    </ul>
                    <p>To exercise any of these rights, please contact us (see below). We may require identity verification before fulfilling your request.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">8. Minors</div>
                <div class="element-text">
                    <p>Our Service is intended only for adults who meet the minimum legal gambling age in their jurisdiction. We do not knowingly collect personal data of minors. If we become aware of a minor's data in our system, we will delete it immediately.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">9. Compliance with Laws</div>
                <div class="element-text">
                    <p>We aim to comply with relevant privacy and data-protection laws, including (where applicable) GDPR (for European/EEA users) and CCPA/CPRA (for users in California, U.S.) and any other regional or jurisdictional data-protection regulations.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">10. Changes to this Policy</div>
                <div class="element-text">
                    <p>We may update this Privacy Policy from time to time (e.g. when laws change or our data practices evolve). When we do, we will post the revised version with a new "Last Updated" date. Continued use of the Service after changes constitutes acceptance of the new policy.</p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">11. Contact Information</div>
                <div class="element-text">
                    <p>If you have any questions, concerns, or requests regarding your personal data — or to exercise your rights — please contact us at:</p>
                    <p>📧 <a href="mailto:support@Worldcupsports.casino">support@Worldcupsports.casino</a></p>
                </div>
            </div>

            <div class="list-element">
                <div class="element-title">⚠️ Important — Legal Disclaimer</div>
                <div class="element-text">
                    <p>This Privacy Policy is a template and does not constitute legal advice. Data-protection requirements vary by country, region, and licensing jurisdiction. Once you decide where Worldcupsports.casino will be licensed and what regions you'll operate in, you should review relevant regulations (e.g. GDPR, CCPA, other local laws), and ideally consult a qualified lawyer to ensure full compliance.</p>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
    export default {
        name: 'ModalPrivacy'
    }
</script>

<style scoped>
    .modal-privacy {
        width: 1000px;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 30px 30px 0 30px;
        border-radius: 15px;
        background: var(--bg-purple-dark);
    }

    .modal-privacy .privacy-header {
        width: 100%;
        padding-bottom: 30px;
        border-bottom: 1px solid #335361;
    }

    .modal-privacy .privacy-header span  {
        font-size: 32px;
        font-weight: 900;
    }

    .modal-privacy .privacy-header p  {
        margin-top: 10px;
        font-size: 14px;
        font-weight: 600;
        color: #bbbfd0;
    }

    .modal-privacy .privacy-list {
        width: 100%;
        height: 600px;
        padding: 30px 20px 30px 0;
        overflow-x: hidden;
        overflow-y: scroll;
    }

    .modal-privacy .privacy-list::-webkit-scrollbar {
        width: 5px;
        height: 0;
    }

    .modal-privacy .privacy-list::-webkit-scrollbar-track {
        margin: 30px 0;
        background-color: #062234;
    }

    .modal-privacy .privacy-list::-webkit-scrollbar-thumb {
        background-color: #1e5164;
    }

    .modal-privacy .list-element {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-top: 25px;
    }

    .modal-privacy .list-element:first-child {
        margin-top: 0;
    }

    .modal-privacy .element-title {
        font-size: 24px;
        font-weight: 800;
        color: #ffffff;
    }

    .modal-privacy .element-text {
        width: 100%;
        margin-top: 15px;
        padding-left: 30px;
    }

    .modal-privacy .element-text p {
        margin-top: 15px;
        font-size: 14px;
        font-weight: 600;
        color: #bbbfd0;
    }

    .modal-privacy .element-text p:first-child {
        margin-top: 0;
    }

    .modal-privacy .element-text ul {
        margin-top: 15px;
        padding-left: 50px;
    }

    .modal-privacy .element-text ul li {
        margin-top: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #bbbfd0;
    }

    .modal-privacy .element-text a {
        color: var(--accent-yellow);
        text-decoration: underline;
        transition: color 0.3s ease;
    }

    .modal-privacy .element-text a:hover {
        color: var(--accent-copper-light);
    }

    .modal-privacy .element-text strong {
        font-weight: 700;
        color: #ffffff;
    }

    @media only screen and (max-width: 1190px) {

        .modal-privacy {
            width: calc(100vw - 20px);
        }

    }

    @media only screen and (max-width: 700px) {

        .modal-privacy {
            padding: 80px 15px 0 15px;
        }

    }
</style>
